import { useState, useEffect, useRef } from "react";

const API = "/api";

const emptyCar = {
  name: "", category: "Economic", description: "", photo: "",
  pricePerDay: "", seats: 5, transmission: "Automatic", available: true
};

const uploadBtnStyle = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "9px 16px", background: "rgba(200,149,108,0.1)",
  color: "#c8956c", border: "1.5px dashed rgba(200,149,108,0.4)",
  borderRadius: 10, fontSize: "0.85rem", cursor: "pointer",
  fontFamily: "DM Sans, sans-serif", marginTop: 8, transition: "all 0.2s"
};

const statusColors = { pending: "#e67e22", confirmed: "#27ae60", cancelled: "#c0392b" };

export default function AdminCars() {
  const [cars, setCars] = useState([]);
  const [carBookings, setCarBookings] = useState([]);
  const [view, setView] = useState("list");
  const [form, setForm] = useState(emptyCar);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const photoRef = useRef();

  const token = localStorage.getItem("adminToken");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchCars();
    fetchCarBookings();
  }, []);

  async function fetchCars() {
    try {
      const res = await fetch(`${API}/rentals/cars/admin`, { headers });
      const data = await res.json();
      setCars(Array.isArray(data) ? data : []);
    } catch (e) { setCars([]); }
  }

  async function fetchCarBookings() {
    try {
      const res = await fetch(`${API}/rentals/bookings`, { headers });
      const data = await res.json();
      setCarBookings(Array.isArray(data) ? data : []);
    } catch (e) { setCarBookings([]); }
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/upload`, { method: "POST", headers: authHeaders, body: fd });
      const data = await res.json();
      setForm(f => ({ ...f, photo: data.path || "" }));
    } catch (e) { setErr("Upload failed"); }
    setUploading(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setMsg(""); setErr("");
    try {
      const url = editId ? `${API}/rentals/cars/${editId}` : `${API}/rentals/cars`;
      const method = editId ? "PUT" : "POST";
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Error saving");
      setMsg(editId ? "Car updated!" : "Car added!");
      fetchCars();
      setTimeout(() => setView("list"), 1200);
    } catch (e) { setErr(e.message); }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this car?")) return;
    await fetch(`${API}/rentals/cars/${id}`, { method: "DELETE", headers });
    fetchCars();
  }

  async function handleBookingStatus(id, status) {
    await fetch(`${API}/rentals/bookings/${id}`, { method: "PATCH", headers, body: JSON.stringify({ status }) });
    fetchCarBookings();
  }

  async function handleBookingDelete(id) {
    if (!window.confirm("Delete this booking?")) return;
    await fetch(`${API}/rentals/bookings/${id}`, { method: "DELETE", headers });
    fetchCarBookings();
  }

  function startEdit(car) {
    setForm({
      name: car.name || "", category: car.category || "Economic",
      description: car.description || "", photo: car.photo || "",
      pricePerDay: car.pricePerDay || "", seats: car.seats || 5,
      transmission: car.transmission || "Automatic", available: car.available !== false
    });
    setEditId(car._id);
    setMsg(""); setErr("");
    setView("form");
  }

  function startNew() {
    setForm(emptyCar);
    setEditId(null);
    setMsg(""); setErr("");
    setView("form");
  }

  if (view === "form") return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 32 }}>
        <button className="admin-btn admin-btn-sm" onClick={() => setView("list")}>← Back</button>
        <div className="admin-section-title">{editId ? "Edit Car" : "Add New Car"}</div>
      </div>

      {msg && <div className="admin-success">{msg}</div>}
      {err && <div className="admin-error-msg">{err}</div>}

      <form onSubmit={handleSubmit} onKeyDown={e => { if (e.key === "Enter" && e.target.tagName !== "BUTTON") e.preventDefault(); }}>
        <div className="admin-card">
          <h3 style={{ marginBottom: 20, color: "#1c1712", fontFamily: "Cormorant Garamond, serif", fontWeight: 300, fontSize: "1.3rem" }}>Car Details</h3>

          <div className="admin-grid-2">
            <div className="admin-field">
              <label>Car Name</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Toyota RAV4" required />
            </div>
            <div className="admin-field">
              <label>Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                <option>Economic</option>
                <option>Sedan</option>
                <option>4x4</option>
                <option>Van</option>
              </select>
            </div>
          </div>

          <div className="admin-grid-3">
            <div className="admin-field">
              <label>Price Per Day (€)</label>
              <input type="number" value={form.pricePerDay} onChange={e => setForm(f => ({ ...f, pricePerDay: e.target.value }))} placeholder="45" required />
            </div>
            <div className="admin-field">
              <label>Seats</label>
              <input type="number" value={form.seats} onChange={e => setForm(f => ({ ...f, seats: e.target.value }))} min="1" max="12" />
            </div>
            <div className="admin-field">
              <label>Transmission</label>
              <select value={form.transmission} onChange={e => setForm(f => ({ ...f, transmission: e.target.value }))}>
                <option>Automatic</option>
                <option>Manual</option>
              </select>
            </div>
          </div>

          <div className="admin-field">
            <label>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Comfortable and reliable for all road types..." style={{ minHeight: 80 }} />
          </div>

          <div className="admin-field">
            <label>Car Photo</label>
            <input value={form.photo} onChange={e => setForm(f => ({ ...f, photo: e.target.value }))} placeholder="/uploads/car.jpg" />
            <input type="file" accept="image/*" ref={photoRef} style={{ display: "none" }} onChange={handlePhotoUpload} />
            <button type="button" style={uploadBtnStyle} onClick={() => photoRef.current.click()}>
              {uploading ? "Uploading..." : "📁 Upload Photo"}
            </button>
            {form.photo && <img src={form.photo} alt="preview" style={{ marginTop: 10, height: 100, borderRadius: 8, objectFit: "cover", maxWidth: "100%" }} />}
          </div>

          <div style={{ marginTop: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.9rem", color: "#1c1712" }}>
              <input type="checkbox" checked={form.available} onChange={e => setForm(f => ({ ...f, available: e.target.checked }))} />
              Available for booking
            </label>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <button type="submit" className="admin-btn admin-btn-gold">{editId ? "Save Changes" : "Add Car"}</button>
          <button type="button" className="admin-btn" style={{ background: "transparent", color: "#1c1712", border: "1px solid rgba(28,23,18,0.15)" }} onClick={() => setView("list")}>Cancel</button>
        </div>
      </form>
    </div>
  );

  if (view === "bookings") return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 32 }}>
        <button className="admin-btn admin-btn-sm" onClick={() => setView("list")}>← Back</button>
        <div className="admin-section-title">Car Rental Bookings</div>
      </div>
      <div className="admin-section-sub" style={{ marginBottom: 24 }}>{carBookings.length} booking{carBookings.length !== 1 ? "s" : ""} received</div>

      {carBookings.length === 0 && (
        <div className="admin-card" style={{ textAlign: "center", padding: 60, color: "rgba(28,23,18,0.4)" }}>No bookings yet.</div>
      )}

      {carBookings.map(b => (
        <div key={b._id} className="admin-card" style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600, color: "#1c1712", marginBottom: 4, fontSize: "1rem" }}>{b.name}</div>
              <div style={{ fontSize: "0.85rem", color: "rgba(28,23,18,0.5)", marginBottom: 2 }}>🚗 {b.carName} · {b.carCategory}</div>
              <div style={{ fontSize: "0.85rem", color: "rgba(28,23,18,0.5)", marginBottom: 2 }}>📅 {b.pickupDate} → {b.returnDate}</div>
              <div style={{ fontSize: "0.85rem", color: "rgba(28,23,18,0.5)", marginBottom: 2 }}>📧 {b.email}</div>
              <div style={{ fontSize: "0.85rem", color: "rgba(28,23,18,0.5)" }}>📞 {b.phone}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
              <span style={{ padding: "4px 12px", borderRadius: 100, fontSize: "0.78rem", fontWeight: 600, background: statusColors[b.status] + "18", color: statusColors[b.status], textTransform: "uppercase", letterSpacing: 0.5 }}>
                {b.status}
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                {b.status !== "confirmed" && (
                  <button className="admin-btn admin-btn-sm" style={{ background: "#27ae60", color: "#fff", border: "none" }} onClick={() => handleBookingStatus(b._id, "confirmed")}>Confirm</button>
                )}
                {b.status !== "cancelled" && (
                  <button className="admin-btn admin-btn-sm" style={{ background: "transparent", color: "#c0392b", border: "1px solid #c0392b" }} onClick={() => handleBookingStatus(b._id, "cancelled")}>Cancel</button>
                )}
                <button className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => handleBookingDelete(b._id)}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
        <div>
          <div className="admin-section-title">Cars</div>
          <div className="admin-section-sub">{cars.length} vehicle{cars.length !== 1 ? "s" : ""} in fleet</div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="admin-btn" style={{ background: "transparent", border: "1px solid rgba(200,149,108,0.3)", color: "#c8956c" }} onClick={() => { fetchCarBookings(); setView("bookings"); }}>
            📋 Bookings
            {carBookings.length > 0 && (
              <span style={{ background: "#c8956c", color: "#fff", borderRadius: "50%", padding: "1px 6px", fontSize: "0.75rem", marginLeft: 6 }}>{carBookings.length}</span>
            )}
          </button>
          <button className="admin-btn admin-btn-gold" onClick={startNew}>+ Add Car</button>
        </div>
      </div>

      {cars.length === 0 && (
        <div className="admin-card" style={{ textAlign: "center", padding: 60, color: "rgba(28,23,18,0.4)" }}>
          No cars yet. Click "Add Car" to get started!
        </div>
      )}

      {cars.map(car => (
        <div key={car._id} className="admin-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {car.photo
              ? <img src={car.photo} alt={car.name} style={{ width: 80, height: 60, objectFit: "cover", borderRadius: 8 }} />
              : <div style={{ width: 80, height: 60, background: "rgba(200,149,108,0.08)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem" }}>🚗</div>
            }
            <div>
              <div style={{ fontWeight: 600, color: "#1c1712", marginBottom: 2 }}>{car.name}</div>
              <div style={{ fontSize: "0.82rem", color: "rgba(28,23,18,0.5)" }}>{car.category} · {car.seats} seats · {car.transmission} · €{car.pricePerDay}/day</div>
              <div style={{ fontSize: "0.78rem", marginTop: 2, color: car.available ? "#27ae60" : "#c0392b" }}>{car.available ? "● Available" : "● Unavailable"}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="admin-btn admin-btn-sm" onClick={() => startEdit(car)}>Edit</button>
            <button className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => handleDelete(car._id)}>Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}